import React, { useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloQueryResult } from '@apollo/client';
import { setSuffix } from '@/utils/helpers';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { Popup } from '@/components/UI/Popup/Popup';
import i18n from '@/i18n';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';

type Props<T> = {
  onSuccess?: () => void | Promise<ApolloQueryResult<T>>;
  dataTestId?: string;
};

export type RestoredAdPopupRef = {
  toggle: () => void;
};

const RestoredAdPopup = <T,>(
  { onSuccess, dataTestId }: Props<T>,
  ref: React.Ref<RestoredAdPopupRef>
) => {
  const { t } = useTranslation();
  const [isSuccessPopupOpen, toggleSuccessPopup] = useToggleOpenState();
  const [isLoading, setIsLoading] = useState(false);

  const description = t(setSuffix(i18n, '30', 'pages.ad.ad_published_for'), {
    days: '30',
  });

  useImperativeHandle(
    ref,
    () => ({
      toggle: () => toggleSuccessPopup(),
    }),
    []
  );

  async function onOkClick() {
    if (onSuccess) {
      setIsLoading(true);
      await onSuccess();
      setIsLoading(false);
    }
    toggleSuccessPopup();
  }

  return (
    <Popup
      dataTestId={dataTestId ?? AdPageTestIds.popup.restoredSuccess}
      isOpen={isSuccessPopupOpen}
      close={toggleSuccessPopup}
      icon="check-circle-rounded.svg"
      title={t('pages.ad.ad_is_restored')}
      description={description}
      actions={{
        left: {
          disabled: isLoading,
          text: t('common.ok'),
          onClick: onOkClick,
        },
      }}
    />
  );
};

export const RestoredAdPopupWithRef = React.forwardRef(RestoredAdPopup);
